import {
  Button,
  ButtonGroup,
  Cell,
  Column,
  Content,
  DatePicker,
  Dialog,
  Divider,
  Flex,
  Footer,
  Header,
  Heading,
  Item,
  Picker,
  ProgressCircle,
  Row,
  TableBody,
  TableHeader,
  TableView,
  TextField,
  ToggleButton,
  View,
} from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import { useAddMultipleActivitiesDialog } from './useAddMultipleActivitiesDialog';
import React, { useState } from 'react';
import { Key } from '@adobe/react-spectrum';

export interface IAddMultipleActivitiesDialogProps {
  masterActivity: FPAData;
  onClose: (reload: boolean) => void;
}

export function AddMultipleActivitiesDialog({ masterActivity, onClose }: IAddMultipleActivitiesDialogProps) {
  const { t } = useTranslation();
  const [selectedContext, setSelectedContext] = useState<string>('0');

  const _t = (key: string, props?: Record<string, any>) => t(`${key}`, { ns: 'layout_components', ...props });
  const {
    name,
    setName,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    servicedItemsList,
    selectedServicedItemKeys,
    setSelectedServicedItemKeys,
    activityTemplates,
    selectedActivityType,
    handleActivityTypeChange,
    masterActivityContext,
    loading,
    saving,
    createActivities,
    searchQuery,
    setSearchQuery,
    componentCategoryMap,
  } = useAddMultipleActivitiesDialog({ masterActivity, onClose, selectedContext });

  const handleCreate = async () => {
    await createActivities();
  };

  const handleCancel = () => {
    onClose(false);
  };

  const isFormValid = () => {
    return (
      name.trim() !== '' &&
      selectedServicedItemKeys.size > 0 &&
      (activityTemplates.length === 1 || selectedActivityType !== null)
    );
  };

  const handleContextChange = (context: string) => {
    setSelectedContext(context);
    // TODO: API calling logic will be implemented later
  };

  // Get context options from masterActivityContext
  const getContextOptions = () => {
    if (!masterActivityContext || masterActivityContext.length === 0) {
      return [];
    }
    
    // Extract unique context types from the master activity context
    const contextTypes = masterActivityContext.map((context: FPAData) => ({ id:context.id, name: context.title })).reverse();
    
    // Map context types to display names
    const result = contextTypes.map((type: { id: number; name: string }) => ({
      value: type.id.toString(),
      label: type.name
    }));
    result.unshift({ value: '0', label: 'All' });
    return result;
  };

  const contextOptions = getContextOptions();

  // Set default selected context when options are loaded
  React.useEffect(() => {
    if (contextOptions.length > 0 && !selectedContext) {
      setSelectedContext(contextOptions[0].value);
    }
  }, [contextOptions, selectedContext]);

  return (
    <Dialog>
      <Heading>Add Devices to Service</Heading>
      <Header>Add multiple devices to service at once</Header>
      <Divider />
      <Content>
        {loading ? (
          <Flex width="100%" justifyContent="center" marginTop={10}>
            <ProgressCircle aria-label="Loading…" isIndeterminate />
          </Flex>
        ) : (
          <Flex direction="column" gap="size-200">
            {activityTemplates.length > 1 && (
              <Picker
                label={_t('activity_type')}
                isRequired
                selectedKey={selectedActivityType}
                onSelectionChange={(key) => handleActivityTypeChange(key as number)}
                width="100%"
              >
                {activityTemplates.map((template) => (
                  <Item key={template.activityType}>{template.activityTemplateName}</Item>
                ))}
              </Picker>
            )}
            <TextField
              label={_t('name')}
              value={name}
              onChange={setName}
              isRequired
              autoFocus
              width="100%"
            />

            <Flex direction="row" gap="size-200">
              <DatePicker
                label={_t('date_from')}
                value={dateFrom}
                onChange={setDateFrom}
                width="50%"
              />
              <DatePicker
                label={_t('date_to')}
                value={dateTo}
                onChange={setDateTo}
                width="50%"
              />
            </Flex>


            {contextOptions.length > 0 && (
              <View>
                <Flex direction="column" gap="size-100" marginBottom="size-100">
                  <View>
                    <span style={{ fontSize: '0.9em', fontWeight: 'bold' }}>
                      Context Filter:
                    </span>
                  </View>
                  <Flex 
                    direction="row" 
                    gap="size-50" 
                    wrap
                    UNSAFE_style={{ 
                      overflow: 'visible',
                      flexWrap: 'wrap',
                      maxWidth: '100%'
                    }}
                  >
                    {contextOptions.map((option) => (
                      <ToggleButton
                        key={option.value}
                        isSelected={selectedContext === option.value}
                        onChange={() => handleContextChange(option.value)}
                        UNSAFE_style={{ 
                          flexShrink: 0,
                          minWidth: 'fit-content'
                        }}
                      >
                        {option.label}
                      </ToggleButton>
                    ))}
                  </Flex>
                </Flex>
              </View>
            )}

            <TextField
              label={_t('search_serviced_items')}
              description={_t('search_serviced_items_placeholder')}
              value={searchQuery}
              onChange={setSearchQuery}
              width="100%"
            />

            <View>
              <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-100">
                <Heading level={4} margin={0}>
                  {_t('serviced_items')}
                </Heading>
                <Button variant="primary" isDisabled>
                  + {_t('add')}
                </Button>
              </Flex>
              <TableView
                aria-label={_t('serviced_items')}
                selectionMode="multiple"
                selectedKeys={selectedServicedItemKeys}
                onSelectionChange={(keys) => {
                  if (keys === 'all') {
                    // When "all" is selected, create a Set with all item IDs
                    setSelectedServicedItemKeys(new Set(servicedItemsList.map(item => item.id!)));
                  } else {
                    // Otherwise, use the Set as provided
                    setSelectedServicedItemKeys(keys as Set<Key>);
                  }
                }}
                width="100%"
                height="size-3000"
              >
                <TableHeader>
                  <Column key="refId">{_t('ref_id')}</Column>
                  <Column key="product">{_t('product')}</Column>
                  <Column key="category">{_t('category')}</Column>
                  <Column key="serialNumber">{_t('serial_number')}</Column>
                </TableHeader>
                <TableBody items={servicedItemsList}>
                  {(item) => (
                    <Row key={item.id!}>
                      <Cell>{item.refId || item.id}</Cell>
                      <Cell>{item.name || _t('unnamed_item')}</Cell>
                      <Cell>
                        {item.componentCategory
                          ? componentCategoryMap[item.componentCategory] ?? item.componentCategory
                          : '-'}
                      </Cell>
                      <Cell>{item.itemSerial || '-'}</Cell>
                    </Row>
                  )}
                </TableBody>
              </TableView>
              <View marginTop="size-100">
                <span style={{ fontSize: '0.85em', color: '#6B6B6B' }}>
                  {_t('selected_items_count', { count: selectedServicedItemKeys.size })}
                </span>
              </View>
            </View>
          </Flex>
        )}
      </Content>
      <Footer>
        <ButtonGroup>
          <Button variant="secondary" onPress={handleCancel} isDisabled={saving}>
            {_t('cancel')}
          </Button>
          <Button variant="cta" onPress={handleCreate} isDisabled={!isFormValid() || saving || loading}>
            {saving ? _t('creating') : _t('create')}
          </Button>
        </ButtonGroup>
      </Footer>
    </Dialog>
  );
}

